import S from './LanguageEdit.module.scss';
import C from '../../Resume.module.scss';
import 'react-datepicker/dist/react-datepicker.css';

import React, { Dispatch, SetStateAction, useEffect, useState, FocusEvent } from 'react';
import { LanguageSkillType, ResumeType } from '../../../../../types/ResumeTypes';
import { DateTimeUtils } from '../../../../../utils/DateTimeUtils';
import SelectDateBox from '../../../../common/SelectDateBox';
import { useRecoilValue } from 'recoil';
import { IvyAuthorizationTokenAtom } from '../../../../../recoil/Authorization.recoil';
import SearchDropdown from '../../../../common/SearchDropdown';
import { DummyLanguageSkill } from '../../../../../constants/IvyConstants';
import { ResumeTabsType } from '../../../../../types/ResumeTypes';

interface LanguageSkillEditProps {
  languageSkills: LanguageSkillType[];
  setResume: Dispatch<SetStateAction<ResumeType>>;
  resumeTabs: ResumeTabsType;
}

const LanguageSkillEdit = (props: LanguageSkillEditProps) => {
  const { languageSkills, setResume, resumeTabs } = props;

  const addLanguageSkill = (data: LanguageSkillType) => {
    setResume((prevResume) => {
      const languageSkills = [...prevResume.languageSkills] || [];
      languageSkills.push(data);

      return {
        ...prevResume,
        languageSkills,
      };
    });
  };

  const removeLanguageSkill = (index: number) => {
    setResume((prevResume) => {
      const updatedLanguageSkills = [...prevResume.languageSkills];

      if (updatedLanguageSkills.length === 1) {
        return {
          ...prevResume,
          languageSkills: [],
        };
      } else {
        updatedLanguageSkills.splice(index, 1); // 해당 index의 요소를 제거
        return {
          ...prevResume,
          languageSkills: updatedLanguageSkills,
        };
      }
    });
  };

  useEffect(() => {
    // 없으면 하나 만들어준다.
    if (languageSkills !== undefined && languageSkills.length === 0) {
      // addLanguageSkill(makeLanguageSkill());
    }
  }, [languageSkills]);

  return (
    <>
      <section ref={resumeTabs[3].element} className={`${C.basic_edit_section} wrapper`}>
        <h4 className={`${C.basic_edit_title} h4`}>어학/자격/수상실적</h4>
        <div className={C.basic_wrap}>
          <div className={C.basic_title}>
            <p className="b1">어학사항</p>
          </div>
          <div className={C.basic_input_wrap}>
            {languageSkills?.map((languageSkill, index) => {
              return (
                <React.Fragment key={`language-skill-edit-${index}`}>
                  <LanguageSkillElement
                    index={index}
                    languageSkill={languageSkill}
                    setResume={setResume}
                  />
                  <button
                    className={`${C.plus_minus_button} ${C.minus_button}`}
                    onClick={() => {
                      removeLanguageSkill(index);
                    }}
                  ></button>
                  <hr />
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div className={C.plus_button_area}>
          <button
            className={`${C.plus_minus_button} ${C.plus_button}`}
            onClick={() => {
              addLanguageSkill(DummyLanguageSkill);
            }}
          ></button>
        </div>
        <hr />
      </section>
    </>
  );
};

interface LanguageSkillElementProps {
  index: number;
  languageSkill: LanguageSkillType;
  setResume: Dispatch<SetStateAction<ResumeType>>;
}

const LanguageSkillElement = (props: LanguageSkillElementProps) => {
  const { index, languageSkill, setResume } = props;
  const token = useRecoilValue(IvyAuthorizationTokenAtom);
  const [isGradeFocused, setIsGradeFocused] = useState<boolean>(false);
  const [isCertificateNumberFocused, setIsCertificateNumberFocused] = useState<boolean>(false);
  const [isInstitutionFocused, setIsInstitutionFocused] = useState<boolean>(false);

  const makeLanguageSkill = () => {
    return {
      language: '',
      testName: '',
      grades: [''],
      grade: '',
      certificateNumber: '',
      at: '',
      institution: '',
      description: '',
    } as LanguageSkillType;
  };

  const updateField = (index: number, fieldName: keyof LanguageSkillType, value: string) => {
    setResume((prevResume) => {
      const updatedLanguageSkill = [...prevResume.languageSkills];
      updatedLanguageSkill[index] = {
        ...updatedLanguageSkill[index],
        [fieldName]: value,
      };
      return {
        ...prevResume,
        languageSkills: updatedLanguageSkill,
      };
    });
  };

  const initLanguageSkillField = (index: number, value: string) => {
    setResume((prevResume) => {
      const updatedLanguageSkill = [...prevResume.languageSkills];
      const originLanguage = updatedLanguageSkill[index].language;
      // updatedLanguageSkill[index] = makeLanguageSkill();
      updatedLanguageSkill[index].language = originLanguage;
      updatedLanguageSkill[index].testName = value;
      return {
        ...prevResume,
        languageSkills: updatedLanguageSkill,
      };
    });
  };

  const updateLanguageSkillTestNameField = (index: number, value: string) => {
    initLanguageSkillField(index, value);
  };

  const updateLanguageSkillTestDateField = (value: Date, index: number) => {
    updateField(index, 'at', DateTimeUtils.toDateString(value));
  };

  const blurEventHandle = (setFocused: Function, e: FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setFocused(false);
    } else {
      setFocused(true);
    }
  };

  const checkInputValue = () => {
    if (languageSkill.grade !== '') {
      setIsGradeFocused(true);
    }

    if (languageSkill.certificateNumber !== '') {
      setIsCertificateNumberFocused(true);
    }

    if (languageSkill.institution !== '') {
      setIsInstitutionFocused(true);
    }
  };

  useEffect(() => {
    checkInputValue();
  }, []);

  return (
    <>
      <div className={`${C.basic_edit_input} ${S.language_edit_input}`}>
        {/* 언어 */}
        <select
          name="languageType"
          id="languageType"
          className="input-border input-select"
          value={languageSkill.language}
          onChange={(e) => {
            updateField(index, 'language', e.target.value);
          }}
        >
          <option value="">언어</option>
          <option value="KOREAN">한국어</option>
          <option value="ENGLISH">영어</option>
          <option value="CHINESE">중국어</option>
        </select>
        {/* 시험 */}
        <SearchDropdown
          selectedSearchValue={languageSkill.testName}
          setSelectedSearchValue={updateLanguageSkillTestNameField}
          type={'language-qualification'}
          token={token}
          selectedValueIndex={index}
        />
        {/* 점수 */}
        <div className="input-grp">
          <label
            htmlFor={`language_grade_${index}`}
            className={isGradeFocused ? 'input-label focus-on' : 'input-label'}
          >
            점수/등급
          </label>
          <input
            type="text"
            id={`language_grade_${index}`}
            className="input-text input-border"
            onFocus={() => {
              setIsGradeFocused(true);
            }}
            onBlur={(e) => {
              blurEventHandle(setIsGradeFocused, e);
            }}
            onChange={(e) => {
              updateField(index, 'grade', e.target.value);
              setIsGradeFocused(true);
            }}
            value={languageSkill.grade}
          />
        </div>
        {/* 자격번호 */}
        <div className="input-grp">
          <label
            htmlFor={`language_certificate_${index}`}
            className={isCertificateNumberFocused ? 'input-label focus-on' : 'input-label'}
          >
            자격번호
          </label>
          <input
            type="text"
            id={`language_certificate_${index}`}
            className="input-text input-border"
            onFocus={() => {
              setIsCertificateNumberFocused(true);
            }}
            onBlur={(e) => {
              blurEventHandle(setIsCertificateNumberFocused, e);
            }}
            onChange={(e) => {
              updateField(index, 'certificateNumber', e.target.value);
              setIsCertificateNumberFocused(true);
            }}
            value={languageSkill.certificateNumber}
          />
        </div>
        {/* 취득/응시일 */}
        <div className={C.resume_period_date}>
          <SelectDateBox
            selectedDate={DateTimeUtils.stringToDateType(languageSkill.at, 'YYYY-MM-dd')}
            setSelectedDate={updateLanguageSkillTestDateField}
            type={'language-test-at-date'}
            index={index}
          />
        </div>
        {/* 발급기관 */}
        <div className="input-grp">
          <label
            htmlFor={`language_institution_${index}`}
            className={isInstitutionFocused ? 'input-label focus-on' : 'input-label'}
          >
            발급기관
          </label>
          <input
            type="text"
            id={`language_institution_${index}`}
            className="input-text input-border"
            onFocus={() => {
              setIsInstitutionFocused(true);
            }}
            onBlur={(e) => {
              blurEventHandle(setIsInstitutionFocused, e);
            }}
            onChange={(e) => {
              updateField(index, 'institution', e.target.value);
              setIsInstitutionFocused(true);
            }}
            value={languageSkill.institution}
          />
        </div>
      </div>
    </>
  );
};

export default LanguageSkillEdit;
